/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * The styled components for the widget Search Accounts
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.15.0
 * @since 1.3.0
 */

import styled from 'styled-components';

import { icons } from '../../../../../../../assets';
import { ONBOARDING_DATA_ATTR } from '../../../../../../../modules/onboarding/onboardingConstants';
import { EOnboardingComponentsId } from '../../../../../../../modules/onboarding/types/onboardingTypes';
import { colors, dashboard, fonts } from '../../../../../../../theme';

export const WidgetSearchAccountsLayoutStyled = styled.div.attrs({
    'data-module': 'widgetSearchAccounts',
    [ONBOARDING_DATA_ATTR]: EOnboardingComponentsId.widgetSearchAccounts,
})`
    margin: 24px 40px 0 40px;
`;

export const WidgetSearchAccountsContainerStyled = styled.div`
    position: relative;
`;

export const WidgetSearchAccountsDisplayContainerStyled = styled.div`
    width: 100%;
    position: relative;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    background-color: ${colors.accountSelectDisplayBgColorDefault};
    padding: 11px 56px 11px 20px;
    cursor: pointer;
    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.09);
    gap: 16px;
`;

export interface IWidgetSearchAccountsDisplayItem {
    isColorSecondary?: boolean;
    width?: string;
    minWidth?: string;
}

export const WidgetSearchAccountsDisplayItemStyled = styled.div<IWidgetSearchAccountsDisplayItem>`
    ${props => props.minWidth && `min-width: ${props.minWidth}` };
    ${props => props.width ? `flex: 0 1 ${props.width}` : 'flex: 1'};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${props => props.isColorSecondary ? colors.accountSelectDisplayTextColorSecondary :
        colors.accountSelectDisplayTextColorDefault};
`;

export const WidgetSearchAccountsDisplayIconStyled = styled.img`
    position: absolute;
    right: 16px;
    bottom: 8px;
    width: 32px;
`;

interface IWidgetSearchAccountsInputIconStyled {
    cursor?: string;
}

export const WidgetSearchAccountsInputIconStyled = styled.img<IWidgetSearchAccountsInputIconStyled>`
    position: absolute;
    right: 34px;
    bottom: 28px;
    ${props => props.cursor ? `cursor: ${props.cursor};` : ''}
`;

export const WidgetSearchAccountsDropdownStyled = styled.div`
    position: absolute;
    top: 100%;
    z-index: 10;
    width: 100%;
    background-color: ${colors.pageDashboardSearchAccountsBgColor};
    box-shadow: 0 5px 8px 0 rgba(0, 0, 0, 0.2);
`;

export const WidgetSearchAccountsInputContainerStyled = styled.div`
    position: relative;
    padding: 20px;
    border-bottom: solid 1px ${colors.pageDashboardSearchAccountsBorderColor};
    background-color: ${colors.pageDashboardSearchAccountsBgColor};
`;

export const WidgetSearchAccountsLoaderContainerStyled = styled.div`
    position: absolute;
    bottom: 32px;
    right: 40px;
`;

export const WidgetSearchAccountsHeaderStyled = styled.div`
    display: flex;
    margin-bottom: 8px;
    padding: 0 16px 0 0;
    ${fonts.textSmall};
    color: ${colors.pageDashboardSearchAccountsListHeaderTextColor};
`;

export interface IWidgetSearchAccountsListItem {
    textAlign?: string;
    width?: string;
}

export const WidgetSearchAccountsItemStyled = styled.div<IWidgetSearchAccountsListItem>`
    ${props => props.width ? `flex: 0 1 ${props.width}` : 'flex: 1'};
    box-sizing: border-box;
    text-align: ${props => props.textAlign === 'right' ? 'right' : 'left'};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:first-child {
        padding-left: 40px;
    }

    &:last-child {
        padding-right: 24px;
    }

    & .account-select__list-item--text-bold {
        font-weight: bold;
        background-color: transparent;
    }
`;

export const WidgetSearchAccountsListBodyStyled = styled.div`
    max-height: ${dashboard.widgetSearchAccountsListHeight};
    overflow-y: auto;

    & > ul {
        padding: 0;
        margin: 0;
        list-style: none;
    }
`;

interface IWidgetSearchAccountsListRowStyled {
    isPaddingRight: boolean;
    isSelectedAccount: boolean;
}

export const WidgetSearchAccountsListRowStyled = styled.div<IWidgetSearchAccountsListRowStyled>`
    display: flex;
    position: relative;
    box-sizing: border-box;
    ${fonts.textMain};
    padding: 8px 0;

    ${props => props.isPaddingRight ? 'padding-right: 16px;' : ''}
    &:hover {
        cursor: pointer;
        background-color: ${colors.pageDashboardSearchAccountsListHoverBgColor};
    }

    ${props => props.isSelectedAccount ? `
    &:before {
        position: absolute;
        top: 10px;
        left: 10px;
        content: '';
        width: 20px;
        height: 20px;
        background: url(${icons.checkedIconBlack}) no-repeat center center;
    };` : ''}
`;

export interface IWidgetSearchAccountsListItemStyled {
    width?: number;
    textAlign?: string;
}

export const WidgetSearchAccountsListItemStyled = styled.div<IWidgetSearchAccountsListItemStyled>`
    ${props => props.width ? `flex: 0 1 ${props.width}` : 'flex: 1'};
    box-sizing: border-box;
    text-align: ${props => props.textAlign === 'right' ? 'right' : 'left'};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:first-child {
        padding-left: 40px;
    }

    &:last-child {
        padding-right: 24px;
    }

    & .widget-search-accounts__list-item--text-bold {
        font-weight: bold;
        background-color: transparent;
    }
`;

export const WidgetSearchAccountsListContainerStyled = styled.div`
    padding: 16px 0 8px;
`;

export const WidgetSearchAccountsLoaderLinearContainer = styled.div`
    padding: 20px 32px;

    & > div:not(:last-child) {
        margin-bottom: 8px;
    }
`;

export const WidgetSearchAccountsMessageContainer = styled.div`
    padding: 24px 40px;
    color: ${colors.pageDashboardSearchAccountsTextColorSecondary};
`;

export const WidgetSearchAccountsLinkContainer = styled.div`
    display: inline-block;
    margin-left: 32px;
`;
